import { useMutation } from "@tanstack/react-query";
import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { WorkspaceAPIService } from "@rails_api_base";
import {
  useJwt,
  useRootPath,
  useSessionStore,
} from "~/stores/sessionStore";

// ============================================================================
// Service Hook
// ============================================================================

export function useWorkspaceService() {
  const jwt = useJwt();
  const rootPath = useRootPath();
  return useMemo(
    () => new WorkspaceAPIService({ jwt: jwt ?? "", baseUrl: rootPath ?? "" }),
    [jwt, rootPath]
  );
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useSwitchWorkspace() {
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback((workspaceId: number) => {
    if (isPending) return;
    setIsPending(true);

    const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
    fetch(`/workspaces/${workspaceId}/switch`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken || "" },
      credentials: "same-origin",
    }).then(() => {
      window.location.assign("/");
    }).catch(() => {
      setIsPending(false);
      toast.error("Failed to switch workspace");
    });
  }, [isPending]);

  return { mutate, isPending };
}

export function useCreateWorkspace() {
  const service = useWorkspaceService();

  return useMutation({
    mutationFn: (name: string) => service.create(name),
    onSuccess: (data) => {
      toast.success("Workspace created");

      // Switch to the new workspace
      const csrfToken = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content;
      fetch(`/workspaces/${data.workspace.id}/switch`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken || "" },
        credentials: "same-origin",
      }).then(() => {
        window.location.assign("/");
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to create workspace", { description: error.message });
    },
  });
}

export function useRenameWorkspace() {
  const service = useWorkspaceService();

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => service.update(id, name),
    onMutate: ({ id, name }) => {
      const store = useSessionStore.getState();
      const previous = {
        workspaces: store.workspaces,
        currentWorkspace: store.currentWorkspace,
        accounts: store.accounts,
      };

      // Optimistically update all store locations
      const updatedWorkspaces = store.workspaces.map((w) =>
        w.id === id ? { ...w, name } : w
      );
      const updatedCurrentWorkspace =
        store.currentWorkspace?.id === id
          ? { ...store.currentWorkspace, name }
          : store.currentWorkspace;
      const updatedAccounts = store.accounts.map((account) => ({
        ...account,
        workspaces: account.workspaces.map((w) =>
          w.id === id ? { ...w, name } : w
        ),
      }));

      store.set({
        workspaces: updatedWorkspaces,
        currentWorkspace: updatedCurrentWorkspace,
        accounts: updatedAccounts,
      });

      return { previous };
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previous) {
        useSessionStore.getState().set(context.previous);
      }
      toast.error("Failed to rename workspace", { description: error.message });
    },
  });
}

export function useInviteMember() {
  const jwt = useJwt();
  const rootPath = useRootPath();

  return useMutation({
    mutationFn: async ({ email, workspaceIds }: { email: string; workspaceIds: number[] }) => {
      const response = await fetch(`${rootPath}/api/v1/workspace_members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        credentials: "same-origin",
        body: JSON.stringify({ email, workspace_ids: workspaceIds }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.errors?.[0] || data.error || "Failed to invite member");
      }

      return response.json();
    },
  });
}

export function useDeleteWorkspace() {
  const service = useWorkspaceService();

  return useMutation({
    mutationFn: (id: number) => service.destroy(id),
    onSuccess: (data, deletedId) => {
      if (data.switched_to) {
        // Deleted the current workspace — hard reload to pick up new context
        window.location.reload();
        return;
      }

      const store = useSessionStore.getState();
      const filtered = store.workspaces.filter((w) => w.id !== deletedId);
      store.set({ workspaces: filtered });
      toast.success("Workspace deleted");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete workspace", { description: error.message });
    },
  });
}
